import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ZoomIn, ZoomOut } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import LogoGeoScalaLight from '@/assets/images/logo-principal-light.svg';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.25;
const BASE_PAGE_WIDTH = 760;

interface LegalDocumentPageProps {
  title: string;
  fileUrl: string;
}

export default function LegalDocumentPage({
  title,
  fileUrl,
}: LegalDocumentPageProps) {
  const navigate = useNavigate();
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const pagesContainerRef = useRef<HTMLDivElement | null>(null);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/session/login');
  };

  const handleLoadSuccess = useCallback(
    ({ numPages: total }: { numPages: number }) => {
      setNumPages(total);
      setCurrentPage(1);
    },
    [],
  );

  // current page = the page whose midpoint is closest to the viewport center
  const handleScroll = useCallback(() => {
    const container = pagesContainerRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!container || !scrollContainer) return;

    const pages = Array.from(
      container.querySelectorAll<HTMLElement>('[data-page-number]'),
    );
    if (pages.length === 0) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const viewportCenter = containerRect.top + containerRect.height / 2;
    let closestPage = 1;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const page of pages) {
      const rect = page.getBoundingClientRect();
      const distance = Math.abs(rect.top + rect.height / 2 - viewportCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPage = Number(page.dataset.pageNumber) || 1;
      }
    }

    setCurrentPage(closestPage);
  }, []);

  // body has overflow:hidden globally, so the page owns its scroll container
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;
    scrollContainer.addEventListener('scroll', handleScroll, {
      passive: true,
    });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const zoomOut = () =>
    setZoom((value) => Math.max(MIN_ZOOM, Math.round((value - ZOOM_STEP) * 100) / 100));
  const zoomIn = () =>
    setZoom((value) => Math.min(MAX_ZOOM, Math.round((value + ZOOM_STEP) * 100) / 100));

  return (
    <div
      ref={scrollContainerRef}
      className="h-screen overflow-y-auto bg-[#EEF2FE]"
    >
      <header className="bg-[#EEF2FE]">
        <div className="flex items-center justify-center py-6">
          <img src={LogoGeoScalaLight} alt="Geoescala" className="h-9 w-auto" />
        </div>
        <div className="mx-auto max-w-[1400px] border-b border-[#C9D6F5] px-6" />

        <div className="mx-auto max-w-[1400px] px-6 py-4">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1.5 text-[13px] font-medium text-[#4B5563] transition hover:text-[#1D4ED8]"
          >
            <ChevronLeft className="h-4 w-4" />
            Voltar
          </button>
        </div>
      </header>

      <main className="px-4 pb-24">
        <div ref={pagesContainerRef} className="flex flex-col items-center">
          <Document
            file={fileUrl}
            onLoadSuccess={handleLoadSuccess}
            loading={
              <div className="flex h-[60vh] items-center justify-center text-sm text-[#6B7280]">
                Carregando documento...
              </div>
            }
            error={
              <div className="flex h-[40vh] flex-col items-center justify-center gap-3 text-center">
                <p className="text-sm text-[#6B7280]">
                  Não foi possível exibir o documento no navegador.
                </p>
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-lg bg-[#1E3A6E] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#182F5A]"
                >
                  Abrir PDF em nova aba
                </a>
              </div>
            }
            externalLinkTarget="_blank"
          >
            <div className="flex flex-col items-center gap-6">
              {Array.from({ length: numPages }, (_, index) => (
                <div
                  key={`page_${index + 1}`}
                  data-page-number={index + 1}
                  className="overflow-hidden bg-white shadow-[0_8px_30px_rgba(43,75,192,0.08)]"
                >
                  <Page
                    pageNumber={index + 1}
                    width={BASE_PAGE_WIDTH * zoom}
                    renderTextLayer
                    renderAnnotationLayer
                  />
                </div>
              ))}
            </div>
          </Document>
        </div>
      </main>

      {numPages > 0 && (
        <div className="fixed bottom-8 left-1/2 z-20 -translate-x-1/2">
          <div className="flex items-center gap-6 rounded-lg bg-[#9AA3B8]/80 px-5 py-2.5 text-[13px] font-medium text-white shadow-lg backdrop-blur-sm">
            <span>
              Página {currentPage}/{numPages}
            </span>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={zoomOut}
                disabled={zoom <= MIN_ZOOM}
                className="rounded p-0.5 transition hover:bg-white/20 disabled:opacity-40"
                aria-label="Diminuir zoom"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <span className="min-w-[42px] text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                type="button"
                onClick={zoomIn}
                disabled={zoom >= MAX_ZOOM}
                className="rounded p-0.5 transition hover:bg-white/20 disabled:opacity-40"
                aria-label="Aumentar zoom"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
